import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useLocationIntegrationStatus } from 'app/hooks/useLocationIntegrationStatus'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'
import {
  APPOINTMENT_LABEL,
  APPOINTMENT_TYPES,
  AppointmentTypeKey,
  AvailabilityDay,
  AvailabilityWindow,
  ISO_FORMAT,
  Slot,
  TYPE_QS,
  buildWindowDays,
} from './locationAvailability.utils'

/**
 * Ceiling on how many locations we fetch for at once. Every location costs four
 * live PMS round trips per window, so an org with dozens of locations and no
 * header selection would otherwise queue hundreds of them. The UI says when it
 * has truncated and points at the location selector.
 */
export const MAX_LOCATIONS = 6

/** Live PMS calls in flight at once, across every location and type. */
const FETCH_CONCURRENCY = 4

export type AppointmentTypeAvailability = {
  key: AppointmentTypeKey
  label: string
  /** One entry per day in the requested window. */
  days: AvailabilityDay[]
  /** Round-trip latency of this type's availability call, in ms. */
  apiMs: number | null
  status: `ok` | `error`
}

export type LocationAvailability = {
  locationId: string
  locationName: string
  /** One entry per appointment type in `APPOINTMENT_TYPES` order; null while still loading. */
  appointmentTypes: (AppointmentTypeAvailability | null)[]
  isLoading: boolean
}

export type UseLocationAvailabilityReturn = {
  locations: LocationAvailability[]
  isLoading: boolean
  error: boolean
  /** Locations that matched before the `MAX_LOCATIONS` cap, so the UI can say it truncated. */
  matchedLocations: number
}

/**
 * Results already fetched, keyed by location + type + window. Module-level so a
 * month the user already visited comes back instantly when they step back to it,
 * and so switching between the four view variants re-uses one set of calls
 * rather than re-querying the PMS per tab. The promise itself is cached, which
 * also dedupes two views mounting against the same window at once.
 */
const cache = new Map<string, Promise<AppointmentTypeAvailability>>()

function cacheKey(locationId: string, key: AppointmentTypeKey, window: AvailabilityWindow): string {
  return `${locationId}|${key}|${window.startISO}|${window.endISO}`
}

async function fetchTypeAvailability(
  locationId: string,
  key: AppointmentTypeKey,
  window: AvailabilityWindow,
): Promise<AppointmentTypeAvailability> {
  const qs = TYPE_QS[key]
  // Never ask the PMS about days that have already passed: a month window that
  // contains today starts mid-month for the fetch, and the earlier days render
  // as past either way. One day back holds for a practice in any timezone.
  const todayISO = moment().subtract(1, `day`).format(ISO_FORMAT)
  const fetchStart = window.startISO < todayISO ? todayISO : window.startISO
  const params = new URLSearchParams({ startDate: fetchStart, endDate: window.endISO })
  const url = `/api/scheduling/locations/${encodeURIComponent(locationId)}/agent/availabilities?${params}${qs ? `&${qs}` : ``}`

  const start = performance.now()
  let status: `ok` | `error` = `ok`
  let slots: Slot[] = []
  try {
    const resp = await globalThis.fetch(url, { credentials: `include` })
    const text = await resp.text()
    if (!resp.ok) {
      status = `error`
    } else {
      const body = JSON.parse(text)
      slots = Array.isArray(body?.slots) ? body.slots : []
    }
  } catch (e) {
    logWarn(`Availability fetch failed`, { locationId, key, error: e })
    status = `error`
  }

  return {
    key,
    label: APPOINTMENT_LABEL[key],
    days: buildWindowDays(slots, window),
    apiMs: performance.now() - start,
    status,
  }
}

/** Runs `tasks` with at most `concurrency` of them in flight at a time. */
async function runPool(tasks: (() => Promise<void>)[], concurrency: number): Promise<void> {
  const queue = [...tasks]
  const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
    while (queue.length) {
      const task = queue.shift()
      if (task) await task()
    }
  })
  await Promise.all(workers)
}

/**
 * Fetches open-slot availability over `window` for every location selected in
 * the dashboard header (falling back to every AI Agent location, capped at
 * `MAX_LOCATIONS`), probing the four fixed appointment types.
 *
 * Served by the authenticated SPA endpoint
 * `GET /api/scheduling/locations/{id}/agent/availabilities` (ported to
 * trackable-api in PR #242 / TRK-5278). The type is selected via the
 * `isEmergency` / `isConsult` / `isLimitedExam` query params (hygiene has none)
 * and the range via `startDate` / `endDate`, which is what lets the month
 * stepper look further ahead than the endpoint's default 31-day lookahead.
 * Response shape: `{ slots: [{ date, timeSlots: [{ time, availabilities }] }] }`
 * where `availabilities` is a stringified open-slot count, and days with no
 * openings are omitted entirely. Locations without an appointment-type vendor id
 * configured for a category return an empty slot list for that category (TRK-5886).
 *
 * Each type resolves into state on its own, so a location paints as its four
 * calls land instead of waiting on the slowest one.
 */
export function useLocationAvailability(
  window: AvailabilityWindow,
  /** Pins the hook to one location instead of the header selection. */
  locationId?: string,
): UseLocationAvailabilityReturn {
  const [searchParams] = useSearchParams()
  // Scoped to AI Agent locations so the no-selection fallback below can't land on a
  // location that has no agent (and whose availabilities endpoint would 500).
  const { locationOptions, isLoading: locationsLoading } = useLocationIntegrationStatus([], `AI Agent`)

  const selectedParam = searchParams.get(`locationIds`) ?? ``
  const matched = useMemo(() => {
    const selectedIds = locationId ? [locationId] : compact(selectedParam.split(`,`))
    const all = locationOptions.map((option) => ({ id: String(option.value), name: option.name }))
    return selectedIds.length ? all.filter((location) => selectedIds.includes(location.id)) : all
  }, [locationOptions, selectedParam, locationId])

  const locations = useMemo(() => matched.slice(0, MAX_LOCATIONS), [matched])
  const locationKey = locations.map((location) => location.id).join(`,`)

  const [entries, setEntries] = useState<Record<string, AppointmentTypeAvailability>>({})
  const [error, setError] = useState(false)

  const { startISO, endISO } = window

  useEffect(() => {
    if (!locationKey) return
    let active = true
    setError(false)

    const tasks = locations.flatMap((location) =>
      APPOINTMENT_TYPES.map((key) => async () => {
        if (!active) return
        const k = cacheKey(location.id, key, window)
        let pending = cache.get(k)
        if (!pending) {
          pending = fetchTypeAvailability(location.id, key, window)
          cache.set(k, pending)
        }
        const result = await pending
        // A failed probe is not worth remembering — let the next mount retry it.
        if (result.status === `error`) cache.delete(k)
        if (!active) return
        setEntries((prev) => ({ ...prev, [k]: result }))
      }),
    )

    bg(async () => {
      try {
        await runPool(tasks, FETCH_CONCURRENCY)
      } catch (e) {
        logError(`Error loading location availability`, e)
        if (active) setError(true)
      }
    })

    return () => {
      active = false
    }
    // `locations` and `window` are rebuilt every render; the primitive keys below
    // are what actually decide whether a different set of calls is needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey, startISO, endISO])

  const resolved = useMemo<LocationAvailability[]>(
    () =>
      locations.map((location) => {
        const appointmentTypes = APPOINTMENT_TYPES.map(
          (key) => entries[`${location.id}|${key}|${startISO}|${endISO}`] ?? null,
        )
        return {
          locationId: location.id,
          locationName: location.name,
          appointmentTypes,
          isLoading: appointmentTypes.some((type) => type === null),
        }
      }),
    [locations, entries, startISO, endISO],
  )

  return {
    locations: resolved,
    isLoading: locationsLoading || resolved.some((location) => location.isLoading),
    error,
    matchedLocations: matched.length,
  }
}
